import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg, fmtMoney, fmtQty, fmtDate } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { StatusBadge, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { LogOut, Plus } from "lucide-react";

export default function Portal() {
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ po_number: "", supplier_invoice_number: "", lines: [] });

  const load = useCallback(() => api.get("/portal/summary").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const pickPo = (po_number) => {
    const po = data.pos.find((p) => p.number === po_number);
    setForm({ po_number, supplier_invoice_number: "",
      lines: po ? po.lines.map((l) => ({ item_number: l.item_number, description: l.description, qty: l.received_qty || l.qty, unit_cost: l.unit_cost })) : [] });
  };

  const upload = async () => {
    try {
      await api.post("/portal/invoices", { po_number: form.po_number, supplier_invoice_number: form.supplier_invoice_number,
        lines: form.lines.map((l) => ({ item_number: l.item_number, qty: Number(l.qty), unit_cost: Number(l.unit_cost) })) });
      toast.success(t("pt.uploaded"));
      setOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center text-xs text-muted-foreground">{t("common.loading")}</div>;

  return (
    <div className="min-h-screen bg-background" data-testid="portal-page">
      <header className="h-14 border-b bg-card flex items-center justify-between px-6 sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-primary text-primary-foreground flex items-center justify-center font-black text-sm">M</div>
          <div>
            <div className="font-black text-sm uppercase leading-none">{t("pt.title")}</div>
            <div className="text-[10px] text-muted-foreground" data-testid="portal-supplier-name">{data.supplier?.name}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border">
            {["es", "en"].map((l) => (
              <button key={l} onClick={() => setLang(l)} data-testid={`portal-lang-${l}`}
                className={`px-2.5 py-1 text-[10px] font-bold uppercase ${lang === l ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>{l}</button>
            ))}
          </div>
          <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="portal-logout-button"
            className="flex items-center gap-1.5 text-[11px] font-bold text-primary hover:underline">
            <LogOut size={12} /> {t("common.logout")}
          </button>
        </div>
      </header>

      <main className="p-6 lg:p-8 max-w-6xl">
        <h1 className="text-2xl font-black tracking-tight uppercase mb-1">{t("pt.welcome")}, {user?.name}</h1>
        <p className="text-xs text-muted-foreground mb-6">{data.supplier?.ab_number} · {data.supplier?.tax_id}</p>

        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{t("pt.pos")}</div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="rounded-none text-xs font-bold" data-testid="portal-upload-button">
                <Plus size={13} className="mr-1" /> {t("pt.upload")}
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none sm:max-w-xl">
              <DialogHeader>
                <DialogTitle className="uppercase font-black">{t("pt.upload")}</DialogTitle>
                <DialogDescription className="text-xs">{t("mt.subtitle")}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">OC</Label>
                  <Select value={form.po_number} onValueChange={pickPo}>
                    <SelectTrigger className="rounded-none h-8 text-xs" data-testid="portal-invoice-po-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{data.pos.filter((p) => p.status !== "cancelled").map((p) => <SelectItem key={p.id} value={p.number}>{p.number}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mt.supplierInvoice")}</Label>
                  <Input value={form.supplier_invoice_number} onChange={(e) => setForm({ ...form, supplier_invoice_number: e.target.value })}
                    className="rounded-none h-8 text-xs" data-testid="portal-invoice-number-input" />
                </div>
              </div>
              {form.lines.map((l, i) => (
                <div key={i} className="flex gap-2 items-center text-xs">
                  <span className="flex-1 font-bold">{l.item_number} <span className="font-normal text-muted-foreground">{l.description}</span></span>
                  <Input type="number" value={l.qty} onChange={(e) => {
                    const lines = [...form.lines]; lines[i] = { ...l, qty: e.target.value }; setForm({ ...form, lines });
                  }} className="rounded-none h-7 text-xs w-20" data-testid={`portal-line-qty-${i}`} />
                  <Input type="number" value={l.unit_cost} onChange={(e) => {
                    const lines = [...form.lines]; lines[i] = { ...l, unit_cost: e.target.value }; setForm({ ...form, lines });
                  }} className="rounded-none h-7 text-xs w-28" data-testid={`portal-line-cost-${i}`} />
                </div>
              ))}
              <Button onClick={upload} disabled={!form.po_number || !form.supplier_invoice_number}
                className="rounded-none font-bold" data-testid="portal-invoice-save-button">{t("pt.upload")}</Button>
            </DialogContent>
          </Dialog>
        </div>
        {data.pos.length === 0 ? <Empty text={t("common.noData")} /> : (
          <div className="tbl-wrap mb-8" data-testid="portal-pos-table">
            <table className="tbl">
              <thead><tr>
                <th>{t("common.number")}</th><th>{t("common.status")}</th><th className="num">{t("common.lines")}</th>
                <th className="num">{t("common.total")}</th><th>{t("common.date")}</th>
              </tr></thead>
              <tbody>
                {data.pos.map((p) => (
                  <tr key={p.id}>
                    <td className="font-bold">{p.number}</td>
                    <td><StatusBadge status={p.status} /></td>
                    <td className="num">{p.lines.length}</td>
                    <td className="num font-bold">{fmtMoney(p.total)}</td>
                    <td className="text-muted-foreground">{fmtDate(p.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-2">{t("pt.invoices")}</div>
            {data.invoices.length === 0 ? <Empty text={t("common.noData")} /> : (
              <div className="tbl-wrap" data-testid="portal-invoices-table">
                <table className="tbl">
                  <thead><tr>
                    <th>{t("mt.supplierInvoice")}</th><th>OC</th><th className="num">{t("common.total")}</th>
                    <th>{t("common.status")}</th><th>{t("pt.payment")}</th>
                  </tr></thead>
                  <tbody>
                    {data.invoices.map((inv) => (
                      <tr key={inv.id}>
                        <td className="font-bold">{inv.supplier_invoice_number}</td>
                        <td>{inv.po_number}</td>
                        <td className="num font-bold">{fmtMoney(inv.total)}</td>
                        <td><StatusBadge status={inv.status} /></td>
                        <td className="font-bold">{inv.scheduled_payment_date || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-2">{t("pt.receipts")}</div>
            {data.receipts.length === 0 ? <Empty text={t("common.noData")} /> : (
              <div className="tbl-wrap" data-testid="portal-receipts-table">
                <table className="tbl">
                  <thead><tr>
                    <th>{t("common.number")}</th><th>OC</th><th className="num">{t("common.qty")}</th><th>{t("common.date")}</th>
                  </tr></thead>
                  <tbody>
                    {data.receipts.map((r) => (
                      <tr key={r.id}>
                        <td className="font-bold">{r.number}</td>
                        <td>{r.po_number}</td>
                        <td className="num">{fmtQty(r.lines.reduce((s, l) => s + l.qty, 0))}</td>
                        <td className="text-muted-foreground">{fmtDate(r.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
